/** Frontend catalog for inventory comments. Stored as a comma-separated string. */
export const INVENTORY_COMMENT_THEMES = [
  'Sin Caja',
  'Caja Dañada',
  'Caja Repuesto',
  'Sin Cubrepolvo',
  'Sin Cubrecaja',
  'Falta Accesorios',
  'Incluye Accesorio',
  'Detalle Estetico',
  'Falta Extralaces',
  'Sin Extralaces',
  'Sin Etiqueta',
  'Con Protector de Suela',
  'Set Completo',
] as const;

export type InventoryCommentTheme = (typeof INVENTORY_COMMENT_THEMES)[number];

function normalizeCommentKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

const THEME_BY_KEY = new Map<string, InventoryCommentTheme>(
  INVENTORY_COMMENT_THEMES.map((theme) => [normalizeCommentKey(theme), theme])
);

const COMMENT_ALIASES: Record<string, InventoryCommentTheme> = {
  'caja de repuesto': 'Caja Repuesto',
  'sin cubrepolvos': 'Sin Cubrepolvo',
  'falta extra laces': 'Falta Extralaces',
  'sin extra laces': 'Sin Extralaces',
  'falta accesorio': 'Falta Accesorios',
  'sin accesorios': 'Falta Accesorios',
  'con accesorios': 'Incluye Accesorio',
  'incluye accesorios': 'Incluye Accesorio',
};

export function isInventoryCommentTheme(value: string): value is InventoryCommentTheme {
  return THEME_BY_KEY.has(normalizeCommentKey(value));
}

/** Map a typed or imported label onto the catalog when it matches a theme or alias. */
export function canonicalizeInventoryComment(value: string): string {
  const key = normalizeCommentKey(value);
  if (!key) return '';
  return THEME_BY_KEY.get(key) ?? COMMENT_ALIASES[key] ?? value.trim().replace(/\s+/g, ' ');
}

export function parseInventoryComments(raw: string): string[] {
  if (!raw.trim()) return [];
  return raw.split(',').reduce<string[]>((acc, part) => {
    const label = canonicalizeInventoryComment(part);
    const key = normalizeCommentKey(label);
    if (!key || acc.some((item) => normalizeCommentKey(item) === key)) return acc;
    return [...acc, label];
  }, []);
}

export function serializeInventoryComments(values: readonly string[]): string {
  return parseInventoryComments(values.join(', ')).join(', ');
}
